import {useProducts} from './use-products';
import {Trans} from '../../i18n/trans';
import {Navbar2} from '../../ui/navigation/navbar/navbar2';
import {Link} from 'react-router-dom';
import {Footer} from '../../ui/footer/footer';
import {Fragment, useState} from 'react';
import {UpsellBillingCycle, findBestPrice} from './find-best-price';
import {StaticPageTitle} from '../../seo/static-page-title';
import {PricingTable} from '@common/billing/pricing-table/pricing-table';
import {Product} from '../product';
import './pricing-page.css';

const FAQ_ITEMS = [
  {
    q: 'What are all the features included in these plans?',
    a: 'Each plan includes a set of AI credits, collaboration seats, and access to specific AI models. You can see the full breakdown on each plan card above.',
  },
  {
    q: 'How do credits work?',
    a: 'Credits are used each time you run the AI agent. The amount consumed depends on the complexity of the task. You can monitor usage from your dashboard.',
  },
  {
    q: 'What happens when I run out of credits?',
    a: 'When your credits run out the agent will pause. You can purchase additional credits or wait until your monthly allotment renews.',
  },
  {
    q: 'What comes with my subscription?',
    a: 'Your subscription includes monthly credits, collaboration seats, access to AI models, and all platform features listed in your plan.',
  },
  {
    q: 'What plan is best for me?',
    a: 'If you are exploring, start free. For personal or small-team projects the Core plan is a great fit. For commercial work or larger teams, Pro gives you the power you need.',
  },
  {
    q: 'What is Effort-Based Pricing (pay-as-you-go)?',
    a: 'Effort-Based Pricing lets you pay only for what the AI actually does. Complex tasks cost more credits; simple ones cost fewer.',
  },
  {
    q: 'How can I control what I spend?',
    a: 'You can set a monthly spending cap in your account settings. The agent will stop once the cap is reached so you never get surprised.',
  },
  {
    q: 'How can I see how much I\'m spending?',
    a: 'Your dashboard shows a real-time usage graph and a full transaction history broken down by session.',
  },
  {
    q: 'Do you offer invoicing instead of credit card payments?',
    a: 'Enterprise customers can request invoice-based billing. Reach out to our sales team to set this up.',
  },
  {
    q: 'Do you offer annual plans at a discount?',
    a: 'Yes — switching to annual billing saves up to 10% compared to monthly. Select "Yearly" above to see the discounted prices.',
  },
];

export function PricingPage() {
  const query = useProducts('pricingPage');
  const [selectedCycle, setSelectedCycle] = useState<UpsellBillingCycle>('monthly');

  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Pricing" />
      </StaticPageTitle>
      <Navbar2 topOffsetClass="top-0" />

      <main className="rp-pricing">
        {/* ── Hero ── */}
        <div className="rp-pricing__hero">
          <h1 className="rp-pricing__h1"><Trans message="Pricing" /></h1>
          <p className="rp-pricing__sub"><Trans message="Choose the best plan for you." /></p>

          {/* Monthly / Yearly toggle */}
          <div className="rp-pricing__toggle">
            <button
              className={`rp-pricing__toggle-btn${selectedCycle === 'monthly' ? ' is-active' : ''}`}
              onClick={() => setSelectedCycle('monthly')}
            >
              <Trans message="Monthly" />
            </button>
            <button
              className={`rp-pricing__toggle-btn${selectedCycle === 'yearly' ? ' is-active' : ''}`}
              onClick={() => setSelectedCycle('yearly')}
            >
              <Trans message="Yearly" />
              <YearlySavingsBadge products={query.data?.products} />
            </button>
          </div>
        </div>

        {/* ── Pricing cards ── */}
        <div className="rp-pricing__grid-wrap">
          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
          />
        </div>

        {/* ── Footnote ── */}
        <p className="rp-pricing__note">
          <Trans message="*Prices are subject to tax depending on your location. The AI agent is powered by large language models. While it can produce powerful results, its behaviour is probabilistic — meaning it may occasionally make mistakes." />
        </p>

        {/* ── FAQ ── */}
        <FaqSection />

        {/* ── Still have questions ── */}
        <div className="rp-pricing__contact">
          <p className="rp-pricing__contact-text">
            Still have questions?{' '}
            <Link to="/billing" className="rp-pricing__contact-link">Billing docs</Link>
            {'. '}
            If you're interested in the Enterprise plan,{' '}
            <Link to="/contact" className="rp-pricing__contact-link">contact us</Link>
            {'. '}
            For support, sign in and use the help button.
          </p>
        </div>
      </main>

      <Footer />
    </Fragment>
  );
}

/* ── Yearly savings badge ── */
function YearlySavingsBadge({products}: {products?: Product[]}) {
  if (!products?.length) return null;
  const pcts = products
    .filter(p => !p.hidden && !p.free)
    .map(p => {
      const mo = findBestPrice('monthly', p.prices);
      const yr = findBestPrice('yearly', p.prices);
      if (!mo || !yr) return 0;
      return Math.round(((mo.amount * 12 - yr.amount) / (mo.amount * 12)) * 100);
    })
    .filter(n => n > 0);
  if (!pcts.length) return null;
  const best = Math.max(...pcts);
  return <span className="rp-pricing__toggle-pill">Up to {best}% off</span>;
}

/* ── FAQ accordion ── */
function FaqSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="rp-faq">
      <div className="rp-faq__inner">
        <h2 className="rp-faq__title"><Trans message="Frequently asked questions" /></h2>
        <ul className="rp-faq__list">
          {FAQ_ITEMS.map((item, i) => (
            <li key={i} className={`rp-faq__item${open === i ? ' is-open' : ''}`}>
              <button
                className="rp-faq__q"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                <span className="rp-faq__q-text">{item.q}</span>
                <span className="rp-faq__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </span>
              </button>
              {open === i && (
                <div className="rp-faq__a">{item.a}</div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
